import React, { useEffect, useState } from 'react';
import TopBar from '../../Components/TopBar';
import { useCalendarContext } from './Provider';
import Intro from './Modals/Intro';
import { classNames } from '../../Services/helpers';
import DayAppointments from './DayAppointments';
import Page from '../../Components/Containers/Page';
import Dropdown from '../../Components/Dropdown';
import Icon from '../../Components/Icons/Icon';
import { DragDropContext } from "@hello-pangea/dnd";
import { useGrants } from '../../Data/Business';
import { PageLoading } from '../../Components/Loading';
import DayHeader from './DayHeader';
import FirstLook from './FirstLook';
import StretchBtn from '../../Components/TopBar/StretchBtn';

const New = () => {
    const {
        calendar, days,
        hasOptimisationIssues, setOptimisationIssuesOpen,
        setFilling, setSchedulingWeek,
        setCurrentlyDragging, moveJob,
    } = useCalendarContext();
    const {activeGrants}  = useGrants();

    if (!activeGrants || !calendar) {
        return <PageLoading />
    }

    return <>
        <FirstLook />

        <TopBar title="Calendar"
            right={<RightTopBar {...{activeGrants}} />}
            shadow={false}
        />

        <JumpToThisWeek />

        <Page zeroPad>
            <DragDropContext
                onDragEnd={e => {
                    const [jobId, customerId] = e.draggableId.split(':');
                    if (!e.destination?.droppableId) {
                        setCurrentlyDragging(null);
                        return;
                    }

                    const [userId, date] = e.destination.droppableId.split(':');
                    moveJob(jobId, customerId, userId, date);
                    setCurrentlyDragging(null);
                }}
                onBeforeDragStart={e => setCurrentlyDragging(e.draggableId)}
                autoScrollerOptions={{disabled: false}}
            >

                <div className={classNames(
                    'flex flex-row items-stretch',
                    activeGrants.length >= 2 ? 'divide-x-2' : 'divide-x'
                )}>
                    {days.map((day, i) => {
                        let scheduledWithoutAppointments = null;
                        if (calendar?.jobs && calendar?.scheduledWithoutAppointments?.[day.date]) {
                            scheduledWithoutAppointments = calendar.jobs.filter(
                                job => calendar.scheduledWithoutAppointments[day.date].indexOf(job.id) > -1
                            );
                        }

                        return <div key={day.date} className="flex flex-col">
                            <DayHeader {...{day, appointments: calendar?.appointments?.appointments?.[day.date] ?? null}} />
                            <DayAppointments key={`appointments-${day.date}`}
                                day={day}
                                appointments={calendar?.appointments?.appointments?.[day.date] ?? null}
                                scheduledWithoutAppointments={scheduledWithoutAppointments}
                            />
                        </div>
                    })}
                </div>

            </DragDropContext>
        </Page>

        {/* Buttons */}
        <div className="absolute bottom-[6.3rem] md:bottom-4 right-0 z-30">
            <div className={classNames(
                'float-right mr-2 md:mr-4 w-auto p-2 rounded-xl backdrop-blur-md bg-slate-200/40 border border-white shadow-lg ring-1 ring-brand',
                'flex flex-row items-center gap-2'
            )}>
                {hasOptimisationIssues &&
                    <button className="btn-sm md:btn btn-warning shadow" onClick={() => setOptimisationIssuesOpen(true)}>
                        <Icon icon="TriangleExclamation" theme="solid" size="0.5x" /> Incomplete
                    </button>
                }

                <button className="btn-sm md:btn btn-secondary shadow" onClick={() => setFilling(true)}>
                    <Icon icon="WandMagicSparkles" className="mr-1" /> Plan
                </button>
                <button className="btn-sm md:btn btn-primary shadow" onClick={() => setSchedulingWeek(true)}>
                    <Icon icon="CalendarCheck" className="mr-1" /> Schedule
                </button>
            </div>
        </div>

        <Intro />
    </>
}

const JumpToThisWeek = () => {
    const {selectedWeek, thisWeek, changeWeek} = useCalendarContext();
    const [hideJumpToThisWeek, setHideJumpToThisWeek] = useState(false);

    useEffect(() => {
        setHideJumpToThisWeek(false);
    }, [selectedWeek, thisWeek]);

    if (selectedWeek === thisWeek || hideJumpToThisWeek) {
        return null;
    }

    return <div
        className="relative top-0 left-0 w-full py-1 bg-brand text-white text-center shadow-inner cursor-pointer"
        onClick={() => changeWeek(thisWeek)}
    >
        Jump to this week

        <Icon icon="CircleX"
            theme="solid"
            size="1x"
            className="fill-white float-right top-1 right-2"
                onClick={() => setHideJumpToThisWeek(true)}
            />
    </div>
}

const RightTopBar = ({activeGrants}) => {
    const {weekOptions, durationSized, setDurationSized, shownGrants, setShownGrants} = useCalendarContext();
    const limitedWeekOptions = weekOptions.slice(2, 7);
    const [showMoreWeeks, setShowMoreWeeks] = useState(false);

    const grantOptions = activeGrants.map(grant => {
        return {
            title: grant.title,
            icon: shownGrants[grant.id] ? 'CircleCheck' : 'Circle',
            onClick: () => {
                setShownGrants({
                    ...shownGrants,
                    [grant.id]: !shownGrants[grant.id]
                });
            },
            preventClosing: true,
        }
    });
    grantOptions.push({
        title: 'Unassigned',
        icon: shownGrants['unassigned'] ? 'CircleCheck' : 'Circle',
        onClick: () => {
            setShownGrants({
                ...shownGrants,
                'unassigned': !shownGrants['unassigned']
            });
        },
        preventClosing: true,
    })
    return <>
        <StretchBtn pref="calendar:widthExpanded" />
        <Dropdown options={[
            {
                section: 'Show users',
            },
            ...grantOptions,
            {
                section: 'Change week',
            },
            ...(showMoreWeeks ? weekOptions : limitedWeekOptions),
            {
                title: `Show ${showMoreWeeks ? 'fewer' : 'more'} weeks`,
                onClick: () => {
                    setShowMoreWeeks(!showMoreWeeks);
                },
                preventClosing: true,
                small: true,
            },
            {
                div: true,
            },
            {
                title: 'Duration sized',
                icon: durationSized ? 'CircleCheck' : 'Circle',
                onClick: () => setDurationSized(!durationSized)
            }
        ]} />
    </>
}

export default New;
